/** Neue Anfrage modal — partner si žádá o nacenění nové zakázky.
 *  MVP: jednoduchý form + TČ konfigurace (značka/model/balíček/typ) + tech params.
 *  v0.2: multi-step wizard + photos + dokumenty. */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Ic } from "@/components/ui/icons";
import { authedFetch } from "@/lib/auth";
import { EDGE_FUNCTION_BASE } from "@/lib/supabase";
import { fetchTcConfigs } from "@/lib/fetchers";
import { translatePackageUnit, translateSystemType } from "@/lib/tc-translations";

const SYSTEM_TYPES = [
  { value: "TC", label: "Wärmepumpe" },
  { value: "FVE", label: "PV-Anlage" },
  { value: "TC_FVE", label: "Wärmepumpe + PV" },
  { value: "OTHER", label: "Sonstiges" },
];

const HEATING_CIRCUITS = [1, 2, 3, 4, 5, 6, 7, 8];

interface NewRequestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewRequestModal({ open, onOpenChange }: NewRequestModalProps) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    title: "",
    description: "",
    system_type: "TC",
    maker: "",
    required_installation_date: "",
    installation_address: "",
    installation_city: "",
    installation_contact_name: "",
    installation_contact_phone: "",
    installation_contact_email: "",
    // TČ konfigurace (volitelné)
    tc_brand: "",
    tc_model: "",
    tc_package_unit: "",
    tc_system_type: "",
    // Technické parametry
    topne_okruhy_count: "",
    podlahove_topeni: "", // "" | "yes" | "no"
    redukcni_ventil: "", // "" | "yes" | "no"
  });

  const tcQ = useQuery({
    queryKey: ["tc_system_configs"],
    queryFn: fetchTcConfigs,
    enabled: form.system_type === "TC" || form.system_type === "TC_FVE",
  });

  const tcBrands = useMemo(() => {
    const set = new Set<string>();
    (tcQ.data || []).forEach((c) => set.add(c.brand));
    return Array.from(set).sort();
  }, [tcQ.data]);

  const tcModels = useMemo(() => {
    if (!form.tc_brand) return [];
    const set = new Set<string>();
    (tcQ.data || []).filter((c) => c.brand === form.tc_brand).forEach((c) => set.add(c.model));
    return Array.from(set).sort();
  }, [tcQ.data, form.tc_brand]);

  const tcPackages = useMemo(() => {
    if (!form.tc_brand || !form.tc_model) return [];
    const set = new Set<string>();
    (tcQ.data || [])
      .filter((c) => c.brand === form.tc_brand && c.model === form.tc_model && c.package_unit)
      .forEach((c) => c.package_unit && set.add(c.package_unit));
    return Array.from(set).sort();
  }, [tcQ.data, form.tc_brand, form.tc_model]);

  const tcSystemTypes = useMemo(() => {
    if (!form.tc_brand || !form.tc_model) return [];
    const set = new Set<string>();
    (tcQ.data || [])
      .filter((c) => c.brand === form.tc_brand && c.model === form.tc_model && c.system_type)
      .forEach((c) => c.system_type && set.add(c.system_type));
    return Array.from(set).sort();
  }, [tcQ.data, form.tc_brand, form.tc_model]);

  const update = <K extends keyof typeof form>(key: K, value: string) => {
    setForm((s) => ({ ...s, [key]: value }));
  };

  const reset = () => {
    setForm({
      title: "",
      description: "",
      system_type: "TC",
      maker: "",
      required_installation_date: "",
      installation_address: "",
      installation_city: "",
      installation_contact_name: "",
      installation_contact_phone: "",
      installation_contact_email: "",
      tc_brand: "",
      tc_model: "",
      tc_package_unit: "",
      tc_system_type: "",
      topne_okruhy_count: "",
      podlahove_topeni: "",
      redukcni_ventil: "",
    });
    setError(null);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const submit = async () => {
    setError(null);
    if (form.title.trim().length < 3) {
      setError("Bezeichnung muss mindestens 3 Zeichen enthalten.");
      return;
    }
    if (form.description.trim().length < 10) {
      setError("Beschreibung muss mindestens 10 Zeichen enthalten.");
      return;
    }
    if (!form.required_installation_date) {
      setError("Bitte den Wunschtermin der Installation angeben.");
      return;
    }
    setSubmitting(true);
    try {
      const resp = await authedFetch<{
        ok: boolean;
        deal_id: string;
        deal_number: string;
        task_id: string | null;
        kam_assigned: boolean;
      }>(`${EDGE_FUNCTION_BASE}/partner-portal/create-request`, {
        method: "POST",
        body: JSON.stringify(form),
      });
      toast.success(
        resp.kam_assigned
          ? `Anfrage ${resp.deal_number} gesendet. Key Account Manager wurde benachrichtigt.`
          : `Anfrage ${resp.deal_number} gesendet.`,
      );
      reset();
      onOpenChange(false);
      navigate(`/orders/${resp.deal_number}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} width={560}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginBottom: 18,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11.5,
              color: "var(--gold)",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              fontWeight: 600,
              marginBottom: 6,
            }}
          >
            Neue Anfrage
          </div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Auftrag anfragen</h2>
          <div style={{ marginTop: 4, fontSize: 12.5, color: "var(--text-3)" }}>
            Wir kalkulieren und melden uns mit dem Angebot.
          </div>
        </div>
        <button
          type="button"
          onClick={close}
          className="btn btn-ghost btn-sm"
          style={{ padding: 6 }}
          aria-label="Schließen"
        >
          <Ic.Close size={16} />
        </button>
      </div>

      <div
        style={{ display: "grid", gap: 14, maxHeight: "65vh", overflowY: "auto", paddingRight: 4 }}
      >
        <Field label="Bezeichnung *">
          <input
            className="input"
            value={form.title}
            onChange={(e) => update("title", e.target.value)}
            placeholder="z.B. Wärmepumpe Familie Müller, Berlin"
            disabled={submitting}
          />
        </Field>

        <Field label="System">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6 }}>
            {SYSTEM_TYPES.map((s) => {
              const active = form.system_type === s.value;
              return (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => update("system_type", s.value)}
                  disabled={submitting}
                  style={{
                    padding: "10px 12px",
                    fontSize: 13,
                    textAlign: "left",
                    borderRadius: 8,
                    background: active ? "var(--gold-12)" : "var(--bg-base-2)",
                    color: active ? "var(--gold-hi)" : "var(--text)",
                    border: `1px solid ${active ? "var(--gold)" : "var(--border)"}`,
                    cursor: "pointer",
                  }}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </Field>

        {(form.system_type === "TC" || form.system_type === "TC_FVE") && (
          <div
            style={{
              border: "1px solid var(--border-subtle)",
              borderRadius: "var(--r-md)",
              padding: 14,
              display: "grid",
              gap: 10,
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: "var(--gold)",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                fontWeight: 600,
              }}
            >
              Konfiguration der Wärmepumpe
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <SelectField
                label="Marke"
                value={form.tc_brand}
                onChange={(v) => {
                  update("tc_brand", v);
                  update("tc_model", "");
                  update("tc_package_unit", "");
                  update("tc_system_type", "");
                }}
                options={tcBrands}
                placeholder="– Marke wählen –"
                disabled={submitting}
              />
              <SelectField
                label="Modell"
                value={form.tc_model}
                onChange={(v) => {
                  update("tc_model", v);
                  update("tc_package_unit", "");
                  update("tc_system_type", "");
                }}
                options={tcModels}
                placeholder="– Modell wählen –"
                disabled={submitting || !form.tc_brand}
              />
              <SelectField
                label="Paket / Inneneinheit"
                value={form.tc_package_unit}
                onChange={(v) => update("tc_package_unit", v)}
                options={tcPackages}
                labelFor={translatePackageUnit}
                placeholder="– Paket wählen –"
                disabled={submitting || !form.tc_model}
              />
              <SelectField
                label="Systemtyp"
                value={form.tc_system_type}
                onChange={(v) => update("tc_system_type", v)}
                options={tcSystemTypes}
                labelFor={translateSystemType}
                placeholder="– Typ wählen –"
                disabled={submitting || !form.tc_model}
              />
            </div>
          </div>
        )}

        {form.system_type !== "TC" && form.system_type !== "TC_FVE" && (
          <Field label="Hersteller / Modell (optional)">
            <input
              className="input"
              value={form.maker}
              onChange={(e) => update("maker", e.target.value)}
              placeholder="z.B. SMA + Trina"
              disabled={submitting}
            />
          </Field>
        )}

        {(form.system_type === "TC" || form.system_type === "TC_FVE") && (
          <div
            style={{
              border: "1px solid var(--border-subtle)",
              borderRadius: "var(--r-md)",
              padding: 14,
              display: "grid",
              gap: 10,
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: "var(--gold)",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                fontWeight: 600,
              }}
            >
              Technische Parameter
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <SelectField
                label="Anzahl Heizkreise"
                value={form.topne_okruhy_count}
                onChange={(v) => update("topne_okruhy_count", v)}
                options={HEATING_CIRCUITS.map(String)}
                placeholder="–"
                disabled={submitting}
              />
              <YesNoField
                label="Fußbodenheizung"
                value={form.podlahove_topeni}
                onChange={(v) => update("podlahove_topeni", v)}
                disabled={submitting}
              />
              <YesNoField
                label="Druckminderer im Haus"
                value={form.redukcni_ventil}
                onChange={(v) => update("redukcni_ventil", v)}
                disabled={submitting}
              />
            </div>
          </div>
        )}

        <Field label="Beschreibung *">
          <textarea
            className="input"
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            placeholder="Was soll installiert werden? Aktuelle Heizung, Größe Objekts, …"
            style={{ height: 100, paddingTop: 10, resize: "none", lineHeight: 1.5 }}
            disabled={submitting}
          />
        </Field>

        <Field label="Wunschtermin Installation *">
          <input
            type="date"
            className="input"
            value={form.required_installation_date}
            onChange={(e) => update("required_installation_date", e.target.value)}
            min={new Date().toISOString().slice(0, 10)}
            disabled={submitting}
            style={{ height: 38 }}
          />
        </Field>

        <Field label="Installationsadresse">
          <input
            className="input"
            value={form.installation_address}
            onChange={(e) => update("installation_address", e.target.value)}
            placeholder="Straße, Hausnummer"
            disabled={submitting}
          />
        </Field>

        <Field label="Kontakt vor Ort">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            <input
              className="input"
              value={form.installation_contact_name}
              onChange={(e) => update("installation_contact_name", e.target.value)}
              placeholder="Name"
              disabled={submitting}
            />
            <input
              className="input"
              value={form.installation_contact_phone}
              onChange={(e) => update("installation_contact_phone", e.target.value)}
              placeholder="Telefon"
              disabled={submitting}
            />
          </div>
        </Field>

        <div style={{ fontSize: 11.5, color: "var(--text-3)", lineHeight: 1.5 }}>
          Fotos und Dokumente fügen Sie nach dem Anlegen direkt im Auftrag hinzu (Tab „Fotos" /
          „Dokumente"). In der nächsten Version: kompletter Wizard mit Foto-Upload.
        </div>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            marginTop: 14,
            padding: 10,
            fontSize: 12.5,
            color: "var(--red)",
            background: "var(--red-12)",
            border: "1px solid rgba(248,113,113,0.22)",
            borderRadius: "var(--r-md)",
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
        <Button variant="ghost" onClick={close} disabled={submitting}>
          Abbrechen
        </Button>
        <Button variant="primary" onClick={() => void submit()} disabled={submitting}>
          {submitting && <Ic.Loader size={14} className="animate-spin" />}
          Anfrage senden
          {!submitting && <Ic.ArrowRight size={14} />}
        </Button>
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11.5,
          color: "var(--text-3)",
          marginBottom: 6,
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  labelFor,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder?: string;
  disabled?: boolean;
  labelFor?: (raw: string) => string;
}) {
  return (
    <Field label={label}>
      <select
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={{ height: 38, paddingRight: 28 }}
      >
        <option value="">{placeholder || "—"}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {labelFor ? labelFor(o) : o}
          </option>
        ))}
      </select>
    </Field>
  );
}

function YesNoField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <Field label={label}>
      <div style={{ display: "flex", gap: 6 }}>
        {[
          { v: "yes", l: "Ja" },
          { v: "no", l: "Nein" },
        ].map((o) => {
          const active = value === o.v;
          return (
            <button
              key={o.v}
              type="button"
              disabled={disabled}
              onClick={() => onChange(active ? "" : o.v)}
              style={{
                flex: 1,
                padding: "8px 10px",
                fontSize: 12.5,
                fontWeight: 500,
                borderRadius: 8,
                background: active ? "var(--gold-12)" : "var(--bg-base-2)",
                color: active ? "var(--gold-hi)" : "var(--text)",
                border: `1px solid ${active ? "var(--gold)" : "var(--border)"}`,
                cursor: disabled ? "not-allowed" : "pointer",
              }}
            >
              {o.l}
            </button>
          );
        })}
      </div>
    </Field>
  );
}
